<template>
    <div ref="mapContainer" id="mapContainer" class="map-container" :class="drawingActive ? 'crosshair' : 'default'">
        <div class="row legend-container q-ma-sm">

            <Legend />
        </div>

    </div>
    <q-spinner v-if="rendering" class="map-spinner" size="md" color="primary"/>
    <q-spinner v-if="loadingLease" class="map-spinner" size="md" color="primary"/>
    <div id="coordDisp"></div>
    <div id="zoomDisp"></div>
    <div id="None"></div>

    <div v-for="(infoObj, name) in infoDialogs" :key="name" >
        <q-dialog v-model="infoObj.model" persistent>
            <InfoDialog  :html="infoObj.text" :name="infoObj.pName"/>
        </q-dialog>
    </div>

</template>

<script setup lang="ts">
// @ts-nocheck
import { onMounted, ref, watch } from 'vue';
import * as M  from 'maplibre-gl';
import { Protocol } from "pmtiles";
import { useMapItems } from "../composables/useMapItems"
import { useHelpers } from "../composables/useHelpers"
import { layerRootNames } from '../assets/MapTiles.json'
import { isMapboxURL, transformMapboxUrl } from 'maplibregl-mapbox-request-transformer'
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { useRouter, useRoute } from 'vue-router';
import Legend from './Legend.vue';
import InfoDialog from './InfoDialog.vue';
const router = useRouter();
const route = useRoute();

const {  mapStyleInfo, setMapNotes, loadingLease, getNextVersionNumber, setProcessedData, draw, mapZoom, setMapZoom, setDrawZoomWarn, testInBounds, cellData, setLoading, setTileLayers, setUserPolygon, setMap,  setPolyOffMap, setActivePane, drawingActive, setDrawingActive,  setDraw, setMapLoaded,  toggleLayer, userPolygon, rendering} = useMapItems()
const { arrayFindObjectByProp } = useHelpers();

let protocol = new Protocol();
M.addProtocol("pmtiles", protocol.tile);

const mapContainer = ref(null);
// const S3_URL = 'https://marinemap-v2.s3.amazonaws.com'

let S3_URL
if (import.meta.env.VITE_AWSURLS== "nonprod"){ // set in local .env file
  S3_URL = "https://d13t5mciemo75t.cloudfront.net" 
}
else if (import.meta.env.VITE_AWSURLS== "preprod"){ // In Netlify, Build settings build command=  VITE_AWSURLS=preprod vite build
  S3_URL = "https://d13t5mciemo75t.cloudfront.net" 
}
else if (import.meta.env.VITE_AWSURLS== "prod"){ // this is set in package.json when building locally for use on maps.tnc.org
  // // Future state prod
  S3_URL = "https://d2qdv9g8hlabmv.cloudfront.net" 
}



const mapboxKey ="pk.eyJ1IjoidG5jbWFwYm94IiwiYSI6ImNseXgwa2VwaTA0ZGEya3B5bXZvNHk4NHIifQ.Dwhwf5aF8g_ouY61PrZWUg"


// this converts the 'mapbox://' format to URL see:
// https://www.npmjs.com/package/maplibregl-mapbox-request-transformer
const transformRequest = (url, resourceType) => {
  if (isMapboxURL(url)) {
    return transformMapboxUrl(url, resourceType, mapboxKey)
  }
  // Do any other transforms you want
  return {url}
}


onMounted(() => {
    const map = new M.Map({
        attributionControl: false,
        container: mapContainer.value,
        center: [-72, 40], // Initial map center [lng, lat]
        zoom: 6, // Initial zoom level
        canvasContextAttributes: {
            preserveDrawingBuffer: true
        },
        style: "mapbox://styles/tncmapbox/cm2donz4i002201pf3j1tajg1", transformRequest,
        
    });


    map.on('load', () => {
        const canvas = map.getCanvas();
        MapboxDraw.constants.classes.CONTROL_BASE  = 'maplibregl-ctrl';
        MapboxDraw.constants.classes.CONTROL_PREFIX = 'maplibregl-ctrl-';
        MapboxDraw.constants.classes.CONTROL_GROUP = 'maplibregl-ctrl-group';

        // ports are staying on by default
        map.setLayoutProperty('crcs-ne-cas-ports', 'visibility', 'none');


        const draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
                polygon: true,
                trash: false
            },
            styles: drawStyles
        });
        setDraw(draw)
        

        // if small screen make attribution compact, else not
        // starts expanded and collapses on map move
        if (mapContainerWidth.value < 500){
            map.addControl(new M.AttributionControl({compact: true}));
        }
        else{
            map.addControl(new M.AttributionControl({compact: false}));
        }
        // Add zoom and rotation controls to the map
        // Add zoom and rotation controls to the map
        map.addControl(new M.NavigationControl({
            visualizePitch: true,
            visualizeRoll: true,
            showZoom: true,
            showCompass: true
        }), 'top-left');

        const addSourceWithRetry = async (layer, retries = 3, backoff = 300) => {
            for (let i = 0; i < retries; i++) {
                try {
                    const cacheBuster = Date.now() + Math.random();
                    map.addSource(`${layer.source}`, {
                        type: 'vector',
                        url: `pmtiles://${S3_URL}/${layer.source}.pmtiles`// ?v=${cacheBuster}`,
                    });
                    return; // Exit the function if successful
                } catch (error) {
                    // console.log(`Attempt ${i + 1} failed:`, error);
                    if (i < retries - 1) {
                        await new Promise(resolve => setTimeout(resolve, backoff));
                        backoff *= 2; // Exponential backoff
                    } else {
                        console.error(`Failed to add source for ${layer.source} after ${retries} attempts`, error);
                        throw error; // Re-throw to trigger cache clearing
                    }
                }
            }
        } 
         
        const handleCacheCorruption = async () => {
            try {
                // Clear browser cache
                if ('caches' in window) {
                    const cacheNames = await caches.keys();
                    await Promise.all(cacheNames.map(name => caches.delete(name)));
                }
                
                // Remove old map
                map.remove();
                
                // Create new map with fresh transform
                const newMap = new M.Map({
                    attributionControl: false,
                    container: mapContainer.value,
                    center: [-72, 40],
                    zoom: 6,
                    canvasContextAttributes: { preserveDrawingBuffer: true },
                    style: "mapbox://styles/tncmapbox/cm2donz4i002201pf3j1tajg1",
                    transformRequest: (url, resourceType) => {
                        if (isMapboxURL(url)) {
                            return transformMapboxUrl(url, resourceType, mapboxKey);
                        }
                        const cacheBuster = Date.now();
                        return { url: `${url}${url.includes('?') ? '&' : '?'}v=${cacheBuster}` };
                    }
                });
                
                setMap(newMap);
                location.reload(); // Still reload to reinitialize everything properly
                
            } catch (error) {
                location.reload();
            }
        };

        map.on('error', (e) => {
            console.log('Map error:', e);
            if (e.error && (e.error.toString().includes('Failed to fetch') || e.error.toString().includes('Wrong magic number'))) {
                console.warn('Cache corruption detected, attempting graceful recovery...');
                handleCacheCorruption();
            }
        });

        // layerRootNames.forEach((layer) => {
        //     // console.log(`adding ${layer.id}`)
        //     try{
        //         addSourceWithRetry(layer);
        //     }
        //     catch{
        //         window.location.reload();
        //     }
        //     finally{
        //         setMapLoaded(true)
        //         setMap(map)
        //         setLoading(false)
        //     }
        // });

        // async function loadLayers(layerRootNames) {
        //     try {
        //         for (const layer of layerRootNames) {
        //             await new Promise((resolve, reject) => {
        //                 try {
        //                     addSourceWithRetry(layer);
        //                     resolve(void 0); // Pass void to resolve
        //                 } catch (error) {
        //                     window.location.reload();
        //                     reject(error);
        //                 }
        //             });
        //         }
        //         setMapLoaded(true);
        //         setMap(map);
        //         setLoading(false);
        //     } catch (error) {
        //         console.error('Error loading layers:', error);
        //     }
        // }
        async function loadLayers(layerRootNames) {
            try {
                for (const layer of layerRootNames) {
                    try {
                        // Add the layer
                        addSourceWithRetry(layer);

                        // Pause 50 ms before adding the next one
                        await new Promise((resolve) => setTimeout(resolve, 5));
                    } catch (error) {
                        // Your existing reload behavior
                        window.location.reload();
                        throw error;
                    }
                }

                setMapLoaded(true);
                setMap(map);
                setLoading(false);
            } catch (error) {
                console.error('Error loading layers:', error);
            }
            }

        // Call the function
        loadLayers(layerRootNames);

        

        map.on('draw.modechange', (e) => {
            const mode = draw.getMode();
            // console.log('Current mode:', mode);

            if (mode === 'draw_polygon') {
                setDrawingActive(true);
            } else {
                setDrawingActive(false);
            }

        });


        
    })

    map.on('mousemove', function (e) {
        var latit = Math.round(e.lngLat.lat * 10000) / 10000
        var longit = Math.round(e.lngLat.lng * 10000) / 10000
        document.getElementById('coordDisp').innerHTML = "Lat= " + latit + " Long= " + longit;
    });

    // some of the sprites are missing from the Mapbox studio basemap.  But they aren't needed, so just remove the assocaited layer
    map.on('styleimagemissing', function(e) {
            // Find the layer that uses the missing image
            var layers = map.getStyle().layers;
            for (var i = 0; i < layers.length; i++) {
                if (layers[i].layout && layers[i].layout['icon-image'] === 'ca-novascotia-alt-2') {
                    // Remove the layer
                    map.removeLayer(layers[i].id);
                    // Optionally, remove the source if it's no longer needed
                    if (layers[i]['source']) {
                        map.removeSource(layers[i]['source']);
                    }
                }
            }
    });

    map.on('zoom', function (e) {
        const zoomLevel = Math.round(map.getZoom() * 10) / 10
        document.getElementById('zoomDisp').innerHTML = "Zoom= " + zoomLevel;
        setMapZoom(map.getZoom())
    });

    map.on('draw.create', function (e) {
        // reset mode manually: need due to control over it when hovering on layer 
        map.getCanvas().style.cursor = 'default';


        // console.log(e)
        // console.log(cellData.value)
        const version = getNextVersionNumber();      
        setUserPolygon(e, version)
        
        // toggling the layer triggers a call to getData(), so no need to call directly here
        // and with all new polys always start with flags
        const layer = arrayFindObjectByProp(tileLayers, 'id', "FLAGS")
        toggleLayer(true, layer, null)
        const inBounds = testInBounds()
        if (inBounds == 'straddle'){infoDialogs.value.straddle.model = true}
        else if (inBounds == 'outside'){
            infoDialogs.value.outside.model = true
            draw.value.trash()
        }
        setProcessedData(version, 'inBounds', null, null, inBounds)
        // setActivePane('summary')
    });

    map.on('moveend', function (e) {
        // If there's a user polygon and they are looking at results,
        // track if the polygon is on or off the map.
        // This is used to zoom back to the polygon on data layer change
        if (userPolygon.value && route.fullPath.includes('results')){
            setPolyOffMap(map, userPolygon.value)
        }
    })

    // add a spinner to the map if a layer is drawing
    const onRender = () =>{rendering.value = true;}
    const endRender = () =>{rendering.value = false;}
    map.on('render', onRender)
    map.on('idle', endRender)

    const tileLayers = []
    layerRootNames.forEach((layer) => {

        tileLayers.push(
            {
                id: `${layer.id}`,
                source: `${layer.source}`,
                'source-layer': `${layer.layer}`,
                type: 'fill',
                displayOnly: layer.displayOnly,
                paint: {
                    'fill-color': '#888888',
                    'fill-outline-color':"#888888",
                    'fill-opacity':0.1,
                    // this sets outline to highlight when the 'highlight' property is applied.  On click, from Select
                    // 'fill-outline-color': [ 
                    //     'case',
                    //     ['boolean', ['feature-state', 'highlight'], false],
                    //     "#d90bc8",
                    //     "#888888",
                    // ],
                    // 'fill-opacity': [
                    //     'case',
                    //     ['boolean', ['feature-state', 'highlight'], false],
                    //     1,
                    //     0.1,
                    // ]
                },
                visible: false
            }
        )
    })
    setTileLayers(tileLayers)

    //populate initial zoom 
    document.getElementById('zoomDisp').innerHTML = "Zoom= " + Math.round(map.getZoom() * 10) / 10

    const mapContainerWidth = ref(null)
    if (mapContainer.value){
        mapContainerWidth.value = mapContainer.value.getBoundingClientRect().width;
    }

});

const infoDialogs = ref({
  straddle: {
    pName: "Partially Outside Study Area",
    model: false,
    text: "The Area of Interest entered is partially outside the study area.  Results are based on the portion of the AOI within the study area."
  },
  outside: {
    pName: "Outside Study Area",
    model: false,
    text: "The Area of Interest entered is outside the study area.  Please enter another AOI."
  },
})


watch(mapZoom, (nv, ov) =>{
    if (nv < 6){
        if (drawingActive.value){
            draw.value.changeMode('simple_select')
            setDrawZoomWarn(true)
        }

        if (Object.keys(mapStyleInfo.value).length >0){
            setMapNotes(true, "Zoom in to level 6 or closer to view data in the map.")
        }
    }
    else{
        if(drawingActive.value){
            draw.value.changeMode('draw_polygon')
            setDrawZoomWarn(false)
        }
        setMapNotes(false, "Zoom in to level 6 or closer to view data in the map.")
    }

    if (nv < 7){
        setMapNotes(true, "Zoom in to level 7 or closer to view bathymetry.")
    }
    else{
        setMapNotes(false, "Zoom in to level 7 or closer to view bathymetry.")
    }

})
const drawStyles = [
  // ----- ACTIVE / INACTIVE LINE OUTLINES -----
  {
    id: 'gl-draw-lines.cold',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['!=', 'active', 'true']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#3b9ddd',
      'line-width': 2,
      // IMPORTANT: wrap literals
      'line-dasharray': ['literal', [0.2, 2.0]]
    }
  },
  {
    id: 'gl-draw-lines.hot',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#fbb03b',
      'line-width': 2,
      'line-dasharray': ['literal', [0.2, 2.0]]
    }
  },

  // ----- POLYGON OUTLINES / FILL -----
  {
    id: 'gl-draw-polygon-stroke.cold',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'active', 'true']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#3b9ddd',
      'line-width': 2
    }
  },
  {
    id: 'gl-draw-polygon-stroke.hot',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon'], ['==', 'active', 'true']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#fbb03b',
      'line-width': 2
    }
  },
  {
    id: 'gl-draw-polygon-fill.cold',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'active', 'true']],
    paint: {
      'fill-color': '#3b9ddd',
      'fill-opacity': 0.1
    }
  },
  {
    id: 'gl-draw-polygon-fill.hot',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon'], ['==', 'active', 'true']],
    paint: {
      'fill-color': '#fbb03b',
      'fill-opacity': 0.1
    }
  },

  // ----- VERTICES -----
  {
    id: 'gl-draw-points.cold',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['!=', 'meta', 'midpoint']],
    paint: {
      'circle-radius': 4,
      'circle-color': '#fbb03b'
    }
  },
  {
    id: 'gl-draw-points.hot',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex'], ['==', 'active', 'true']],
    paint: {
      'circle-radius': 7,
      'circle-color': '#fbb03b'
    }
  },

  // ----- MIDPOINT HANDLES -----
  {
    id: 'gl-draw-midpoint',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: {
      'circle-radius': 4,
      'circle-color': '#fbb03b'
    }
  }
];


</script>

<style >
.crosshair{
  cursor: crosshair;
}
/* .maplibregl-ctrl-top-right{
    position: absolute;
    top: 10px;
    right: 10px;
} */
.map-container {
    width: 100%;
    height: calc(100vh - 104px);
}
#coordDisp {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    bottom: 35px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    font: 12px/20px 'Helvetica Neue', Arial, Helvetica, sans-serif;
    padding: 1px 5px 1px 5px;
    position: absolute;
    left: 25px;
    width: 100px;
    z-index: 50;
}

#zoomDisp {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    bottom: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    font: 12px/20px 'Helvetica Neue', Arial, Helvetica, sans-serif;
    padding: 1px 5px 1px 5px;
    position: absolute;
    left: 25px;
    width: 100px;
    z-index: 50;
}
.map-spinner{
  position: absolute;
  bottom: 50%;
  left: 50%;
  z-index: 100;
}

.maplibregl-ctrl-top-right {
    visibility: hidden;
}

.maplibregl-popup-content{
    max-height: 400px;
    width: 200px;
    min-width: 150px;
    overflow: auto;
    overscroll-behavior: none;
}

.legend-container{
    z-index: 50;
    position: absolute;
    background-color: white;
    min-width: 200px;
    max-width: 400px;
    top: 0px;
    right: 0px; 
}
.copy-errors{
    z-index: 100;
}
</style>